import { useCallback, useContext, useEffect, useState } from "react";
import { db } from "../../index";
import { getTarifsOptions, tripTypeOptions, vehicleOptions } from "../vehicles";
import { UserContext } from "../../App";

export function useTarifsOptions() {
  const { userInfos } = useContext(UserContext);
  const [tarifs, setTarifs] = useState([]);

  useEffect(() => {
    getTarifsOptions(userInfos.uid).then((res) => {
      setTarifs(res);
    });
  }, [userInfos.uid]);

  return tarifs;
}

export function useExistingObject({
  userInfos,
  objectId,
  isEstimate = true,
  tarifs,
}) {
  const [rawObject, setRawObject] = useState(null);

  const initUpdateObject = useCallback(
    async (objectId) => {
      if (!userInfos.jwt || !userInfos.uid || !objectId) return;

      const objRefPart = isEstimate ? "/estimates/" : "/invoices/";
      const snap = await db
        .ref("users/" + userInfos.uid + objRefPart + objectId)
        .once("value");

      if (!snap.exists()) return;
      const rawObject = snap.val();
      rawObject.id = snap.key;

      rawObject.bookings = rawObject.bookings?.map((booking) => {
        const vehicle = vehicleOptions.find((v) => v.value === booking.vehicle);
        const tarif = tarifs.find((t) => t.value === booking.tarif);
        let tarif_product = null;
        if (booking.tarif_product) {
          const product = tarif?.data?.products?.find(p => p.id === booking.tarif_product);
          if (product) {
            tarif_product = {value: product.id, label: product.label};
          }
        }

        const tripType = tripTypeOptions.find(
          (t) => t.value === booking.tripType
        );
        const bookingCustomerLabel =
          booking.customer.firstname + " " + booking.customer.lastname;
        const bookingCustomer = {
          label: bookingCustomerLabel,
          data: booking.customer,
          value: booking.customer.key,
        };

        return {
          ...booking,
          tarif,
          tarif_product,
          pickupAddressInput: booking.pickupAddress,
          dropoffAddressInput: booking.dropoffAddress,
          vehicle,
          tripType,
          customer: bookingCustomer,
        };
      }) || [];

      setRawObject(rawObject);
    },
    [userInfos.jwt, userInfos.uid, isEstimate, tarifs]
  );

  useEffect(() => {
    if (objectId) {
      initUpdateObject(objectId).then();
    }
  }, [initUpdateObject, objectId]);

  return rawObject;
}

export function validateEstimateInvoiceBooking(booking) {
  if (!booking.customer) throw new Error("Passager requis");
  if (!booking.tripType) throw new Error("Type de trajet requis");
  if (!booking.vehicle) throw new Error("Véhicule requis");
  if (!booking.pickupDate) throw new Error("Date de départ requise");
  
  // Validate date format (YYYY-MM-DD for HTML date input)
  if (booking.pickupDate) {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (!dateRegex.test(booking.pickupDate)) {
      throw new Error("Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.");
    }
    
    // Validate that the date is actually valid (not 2025-02-30, etc.)
    const dateParts = booking.pickupDate.split('-');
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10);
    const day = parseInt(dateParts[2], 10);
    const dateObj = new Date(year, month - 1, day);
    
    if (
      dateObj.getFullYear() !== year ||
      dateObj.getMonth() !== month - 1 ||
      dateObj.getDate() !== day ||
      year < 1900 || 
      year > 2100
    ) {
      throw new Error("Le format de la date est invalide. Veuillez saisir une date au format JJ/MM/AAAA.");
    }
  }
  
  if (!booking.pickupTime) throw new Error("Heure de départ requise");
  if (!booking.pickupAddress) throw new Error("Adresse de départ requise");
  if (!booking.dropoffAddress) throw new Error("Adresse d'arrivée requise");
  if (!booking.price) throw new Error("Tarif requis");
}
